import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  formatRelative, formatDateTime, stripMentionIds,
  categoryColor, categoryLabel,
} from "@/lib/constants";
import {
  Search, Plus, CheckSquare, Users,
  MessageSquare, Loader2, ChevronDown, EyeOff,
} from "lucide-react";

// ── Helpers ──────────────────────────────────────────────────────────────────

const initials = (s) =>
  (s || "?").trim().split(/\s+/).map((w) => w[0]).filter(Boolean).join("").slice(0, 2).toUpperCase();

const previewText = (chat) => {
  const t = (chat.last_text || "").replace(/\s+/g, " ").trim();
  if (!t) return "(media)";
  if (chat.chat_type === "group" && chat.last_sender && chat.last_direction !== "outgoing") {
    return `${chat.last_sender.split(" ")[0]}: ${t}`;
  }
  if (chat.last_direction === "outgoing") return `You: ${t}`;
  return t;
};

const STATUS_BG = {
  pending:     "bg-amber-50  border-amber-200  text-amber-800",
  in_progress: "bg-blue-50   border-blue-200   text-blue-800",
  completed:   "bg-emerald-50 border-emerald-200 text-emerald-800",
  cancelled:   "bg-slate-50  border-slate-200  text-slate-600",
};

// ── Page ─────────────────────────────────────────────────────────────────────

export default function Messages() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const activeChatId = params.get("chat");

  const [chats, setChats] = useState([]);
  const [loadingChats, setLoadingChats] = useState(true);
  const [search, setSearch] = useState("");
  const [chatTypeFilter, setChatTypeFilter] = useState("all");  // all | individual | group

  const loadChats = async () => {
    setLoadingChats(true);
    try {
      const r = await api.get("/chats", {
        params: chatTypeFilter !== "all" ? { chat_type: chatTypeFilter } : {},
      });
      setChats(r.data.chats || []);
    } catch {
      toast.error("Failed to load chats");
    } finally {
      setLoadingChats(false);
    }
  };

  useEffect(() => { loadChats(); /* eslint-disable-next-line */ }, [chatTypeFilter]);
  useEffect(() => {
    // Light polling so new messages bubble the chat to the top.
    const t = setInterval(loadChats, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [chatTypeFilter]);

  const filteredChats = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return chats;
    return chats.filter((c) =>
      (c.chat_name || "").toLowerCase().includes(q) ||
      (c.chat_phone || "").includes(q)
    );
  }, [chats, search]);

  const activeChat = useMemo(
    () => chats.find((c) => c.chat_id === activeChatId) || null,
    [chats, activeChatId]
  );

  const selectChat = (id) => setParams({ chat: id }, { replace: true });

  return (
    <div className="flex h-[calc(100vh-4rem)] bg-white" data-testid="chats-page">
      {/* ── Left pane: chat list ── */}
      <aside className="w-[340px] shrink-0 border-r border-slate-200 flex flex-col bg-slate-50/60">
        <div className="px-4 pt-5 pb-3 border-b border-slate-200 bg-white">
          <h1 className="text-[20px] font-extrabold tracking-tight text-slate-900">Chats</h1>
          <div className="mt-3 relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search chats…"
              className="w-full h-9 pl-9 pr-3 text-[13px] border border-slate-200 rounded-lg focus:border-slate-900 outline-none bg-white"
            />
          </div>
          <div className="mt-2 flex gap-1">
            {[
              { k: "all",        l: "All" },
              { k: "individual", l: "Direct" },
              { k: "group",      l: "Groups" },
            ].map((t) => (
              <button
                key={t.k}
                onClick={() => setChatTypeFilter(t.k)}
                className={
                  "text-[11.5px] px-2.5 py-1 rounded-full border transition " +
                  (chatTypeFilter === t.k
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-600 border-slate-200 hover:border-slate-400")
                }
              >
                {t.l}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loadingChats && chats.length === 0 ? (
            <ChatListSkeleton />
          ) : filteredChats.length === 0 ? (
            <div className="p-6 text-center text-slate-400 text-[13px]">
              {search ? "No chats match that search." : "No chats yet."}
            </div>
          ) : (
            filteredChats.map((c) => {
              const isActive = c.chat_id === activeChatId;
              return (
                <button
                  key={c.chat_id}
                  onClick={() => selectChat(c.chat_id)}
                  className={
                    "w-full text-left px-4 py-3 flex gap-3 items-start border-b border-slate-100 transition " +
                    (isActive ? "bg-blue-50/70" : "hover:bg-slate-100/70")
                  }
                  data-testid={`chat-row-${c.chat_id}`}
                >
                  <div className={
                    "w-10 h-10 shrink-0 rounded-full grid place-items-center text-[12.5px] font-bold " +
                    (c.chat_type === "group"
                      ? "bg-violet-100 text-violet-700"
                      : "bg-emerald-100 text-emerald-700")
                  }>
                    {c.chat_type === "group" ? <Users size={16} /> : initials(c.chat_name)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-[13.5px] font-semibold text-slate-900 truncate">
                        {c.chat_name || "Unknown"}
                      </span>
                      <span className="ml-auto text-[10.5px] text-slate-400 font-mono shrink-0">
                        {formatRelative(c.last_at)}
                      </span>
                    </div>
                    <div className="text-[12px] text-slate-500 truncate mt-0.5">
                      {previewText(c)}
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </aside>

      {/* ── Right pane: conversation ── */}
      <main className="flex-1 min-w-0 flex flex-col bg-[#faf8f3]">
        {!activeChat ? (
          <EmptyChatPane />
        ) : (
          <ChatConversation
            key={activeChat.chat_id}
            chat={activeChat}
            onTaskCreated={loadChats}
            onOpenTask={(tid) => nav(`/app/tasks?focus=${tid}`)}
          />
        )}
      </main>
    </div>
  );
}

// ── Skeleton (chat-list loading) ────────────────────────────────────────────

function ChatListSkeleton() {
  return (
    <div className="animate-pulse">
      {Array.from({ length: 7 }).map((_, i) => (
        <div key={i} className="px-4 py-3 flex gap-3 items-start border-b border-slate-100">
          <div className="w-10 h-10 rounded-full bg-slate-200 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <div className="h-3 bg-slate-200 rounded w-2/5" />
              <div className="ml-auto h-2 bg-slate-100 rounded w-10" />
            </div>
            <div className="h-2.5 bg-slate-100 rounded w-4/5 mt-2" />
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Empty state ──────────────────────────────────────────────────────────────

function EmptyChatPane() {
  return (
    <div className="flex-1 grid place-items-center text-center">
      <div>
        <div className="w-16 h-16 mx-auto bg-slate-100 rounded-full grid place-items-center mb-4">
          <MessageSquare size={26} className="text-slate-400" />
        </div>
        <div className="text-[15px] font-semibold text-slate-700">Select a chat to view messages</div>
        <p className="text-[13px] text-slate-500 mt-1 max-w-sm mx-auto">
          Pick any conversation from the left to see its full history, create
          tasks from any message, and reply on WhatsApp.
        </p>
      </div>
    </div>
  );
}

// ── Conversation pane ───────────────────────────────────────────────────────

function ChatConversation({ chat, onTaskCreated, onOpenTask }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const scrollerRef = useRef(null);
  const bottomRef = useRef(null);

  const load = async (opts = {}) => {
    const before = opts.before;
    if (before) setLoadingMore(true);
    else setLoading(true);
    try {
      const r = await api.get(`/chats/${encodeURIComponent(chat.chat_id)}/messages`, {
        params: before ? { before, limit: 60 } : { limit: 60 },
      });
      const fresh = r.data.messages || [];
      setHasMore(!!r.data.has_more);
      if (before) {
        // Prepend older messages, preserve scroll position visually.
        const el = scrollerRef.current;
        const prevHeight = el?.scrollHeight || 0;
        setMessages((prev) => [...fresh.slice().reverse(), ...prev]);
        // Restore offset on next paint.
        requestAnimationFrame(() => {
          if (el) el.scrollTop = el.scrollHeight - prevHeight;
        });
      } else {
        setMessages(fresh.slice().reverse());
        requestAnimationFrame(() => bottomRef.current?.scrollIntoView({ block: "end" }));
      }
    } catch {
      toast.error("Failed to load messages");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [chat.chat_id]);

  const loadOlder = () => {
    if (loadingMore || !hasMore || messages.length === 0) return;
    load({ before: messages[0].created_at });
  };

  const createOrApproveTaskFor = async (m) => {
    // If there's already an in-review task for this message, approve it
    // (promote to a real task) instead of creating a duplicate.
    const inReview = m.task && m.task.in_review_queue;
    try {
      let summary;
      if (inReview) {
        await api.post(`/tasks/${m.task.id}/review`, { action: "approve" });
        summary = {
          id: m.task.id, title: m.task.title, status: "pending",
          priority: m.task.priority, category: m.task.category,
          in_review_queue: false,
        };
        toast.success("Task approved");
      } else {
        const r = await api.post(`/messages/${m.id}/task`, {});
        summary = {
          id: r.data.id, title: r.data.title, status: r.data.status,
          priority: r.data.priority, category: r.data.category,
          in_review_queue: !!r.data.in_review_queue,
        };
        toast.success("Task created");
      }
      // Optimistic patch — replace this message with one carrying its approved task.
      setMessages((prev) => prev.map((x) => x.id === m.id ? { ...x, task: summary } : x));
      onTaskCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (inReview ? "Approve failed" : "Failed to create task"));
    }
  };

  return (
    <>
      {/* Conversation header */}
      <header className="px-5 py-3 border-b border-slate-200 bg-white flex items-center gap-3 shrink-0">
        <div className={
          "w-9 h-9 rounded-full grid place-items-center text-[12px] font-bold shrink-0 " +
          (chat.chat_type === "group" ? "bg-violet-100 text-violet-700" : "bg-emerald-100 text-emerald-700")
        }>
          {chat.chat_type === "group" ? <Users size={15} /> : initials(chat.chat_name)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[14.5px] font-bold text-slate-900 truncate">
            {chat.chat_name || "Unknown"}
          </div>
          <div className="text-[11.5px] text-slate-500 font-mono">
            {chat.chat_type === "group" ? "Group" : (chat.chat_phone || "Direct chat")}
            <span className="mx-1.5 text-slate-300">·</span>
            {chat.message_count} message{chat.message_count === 1 ? "" : "s"}
          </div>
        </div>
      </header>

      {/* Message scroller */}
      <div ref={scrollerRef} className="flex-1 overflow-y-auto px-6 py-5">
        {loading ? (
          <div className="text-center text-slate-400 text-[13px] py-10">Loading messages…</div>
        ) : (
          <>
            {hasMore && (
              <div className="text-center mb-4">
                <button
                  onClick={loadOlder}
                  disabled={loadingMore}
                  className="text-[12px] px-3 py-1.5 border border-slate-200 hover:border-slate-400 bg-white rounded-full inline-flex items-center gap-1 disabled:opacity-50"
                >
                  {loadingMore ? <Loader2 size={12} className="animate-spin" /> : <ChevronDown size={12} className="rotate-180" />}
                  {loadingMore ? "Loading…" : "Load older messages"}
                </button>
              </div>
            )}
            <div className="space-y-2">
              {messages.map((m, i) => (
                <MessageBubble
                  key={m.id}
                  m={m}
                  isGroup={chat.chat_type === "group"}
                  prevSender={messages[i - 1]?.sender_name}
                  onCreateTask={() => createOrApproveTaskFor(m)}
                  onOpenTask={onOpenTask}
                />
              ))}
            </div>
            <div ref={bottomRef} />
          </>
        )}
      </div>

      {/* Read-only footer — WhatsApp ToS compliance: we never send from here */}
      <footer className="border-t border-slate-200 bg-slate-50/60 px-5 py-2.5 flex items-center gap-2 text-[11.5px] text-slate-500 shrink-0">
        <EyeOff size={12} className="text-slate-400" />
        Read-only view — Brainydo never sends messages on your behalf from this page.
      </footer>
    </>
  );
}

// ── Message bubble ───────────────────────────────────────────────────────────

function MessageBubble({ m, isGroup, prevSender, onCreateTask, onOpenTask }) {
  const out = m.direction === "outgoing";
  const showSender = isGroup && !out && m.sender_name && m.sender_name !== prevSender;
  return (
    <div className={"flex group " + (out ? "justify-end" : "justify-start")}>
      <div className={"max-w-[68%] " + (out ? "items-end" : "items-start") + " flex flex-col"}>
        {showSender && (
          <div className="text-[11px] text-slate-500 font-semibold mb-0.5 px-3">
            {m.sender_name}
            {m.sender_phone && <span className="text-slate-400 font-mono ml-1.5">{m.sender_phone}</span>}
          </div>
        )}
        <div
          className={
            "px-3.5 py-2 rounded-[10px] text-[13.5px] leading-[1.45] whitespace-pre-wrap break-words shadow-sm " +
            (out
              ? "bg-[#d9fdd3] text-slate-900 rounded-tr-[2px]"
              : "bg-white text-slate-900 border border-slate-200 rounded-tl-[2px]")
          }
        >
          {stripMentionIds(m.text || "") || <span className="italic text-slate-400">(no text)</span>}
        </div>
        <div className={"mt-1 px-2 flex items-center gap-2 text-[10.5px] text-slate-400 " + (out ? "justify-end" : "justify-start")}>
          <span className="font-mono">{formatDateTime(m.timestamp || m.created_at)}</span>
          {m.classification?.category && (
            <span
              className="px-1.5 py-0.5 border rounded text-[9.5px] tracking-[0.16em] uppercase font-bold"
              style={{ borderColor: categoryColor(m.classification.category), color: categoryColor(m.classification.category) }}
            >
              {categoryLabel(m.classification.category)}
            </span>
          )}
          {/* Task affordance — three states:
              (1) approved task     → status-colored badge, click → open task
              (2) task in review    → amber "Approve & create" button (always visible)
              (3) no task at all    → dashed "Create task" button (on hover) */}
          {m.task && !m.task.in_review_queue ? (
            <button
              onClick={() => onOpenTask(m.task.id)}
              title={m.task.title || "Open task"}
              className={
                "inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold border transition hover:brightness-95 " +
                (STATUS_BG[m.task.status] || "bg-slate-50 border-slate-200 text-slate-600")
              }
              data-testid={`task-badge-${m.task.id}`}
            >
              <CheckSquare size={10} />
              Task · {m.task.status || "pending"}
            </button>
          ) : m.task && m.task.in_review_queue ? (
            <button
              onClick={onCreateTask}
              title="AI suggested a task — approve to create it"
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold border bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100 transition"
              data-testid="approve-review-task"
            >
              <Plus size={10} />
              Approve &amp; create
            </button>
          ) : (
            <button
              onClick={onCreateTask}
              className="opacity-0 group-hover:opacity-100 transition inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold border border-dashed border-slate-300 text-slate-500 hover:text-blue-600 hover:border-blue-400"
              data-testid="create-task-from-message"
            >
              <Plus size={10} />
              Create task
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

